/**
 * Paper Trading Service
 * Location: frontend/src/services/paperTradingService.js
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const apiCall = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: getHeaders()
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  
  return data;
};

export const PaperTradingService = {
  /**
   * Get a quote for a symbol
   */
  getQuote: async (symbol) => {
    const response = await fetch(`${API_URL}/api/stocks/quote/${symbol}`, {
      headers: getHeaders()
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to get quote');
    }
    return data;
  },

  /**
   * Get portfolio with positions and current values
   */
  getPortfolio: () => apiCall('/api/paper/portfolio'),

  /**
   * Execute a paper trade
   */
  executeTrade: ({ symbol, type, quantity }) => 
    apiCall('/api/paper/trade', {
      method: 'POST',
      body: JSON.stringify({ symbol, type, quantity })
    }),

  /**
   * Get transaction history
   */
  getHistory: (limit = 50, symbol = null) => {
    const params = new URLSearchParams({ limit });
    if (symbol) params.append('symbol', symbol);
    return apiCall(`/api/paper/history?${params}`);
  },

  /**
   * Reset paper trading account
   */
  resetAccount: (startingBalance = 100000) => 
    apiCall('/api/paper/reset', {
      method: 'POST',
      body: JSON.stringify({ startingBalance })
    }),

  /**
   * Get performance statistics
   */
  getStats: () => apiCall('/api/paper/stats'),

  /**
   * Get positions (extracted from portfolio)
   */
  getPositions: async () => {
    const data = await apiCall('/api/paper/portfolio');
    return data.positions || [];
  },

  /**
   * Get transactions (alias for getHistory)
   */
  getTransactions: (limit = 50) => apiCall(`/api/paper/history?limit=${limit}`),

  /**
   * Get position for a specific symbol
   */
  getPosition: async (symbol) => {
    const { positions } = await apiCall('/api/paper/portfolio');
    return positions.find(p => p.symbol === symbol.toUpperCase()) || null;
  },

  /**
   * Get quick portfolio summary (for header/widget display)
   */
  getQuickSummary: async () => {
    const data = await apiCall('/api/paper/portfolio');
    return {
      totalValue: data.portfolio.totalValue,
      totalReturn: data.portfolio.totalReturn,
      totalReturnPercent: data.portfolio.totalReturnPercent,
      cashBalance: data.portfolio.cashBalance,
      positionCount: data.positions.length
    };
  }
};

export default PaperTradingService;